<script setup lang="ts">
import { $isQuoteNode } from "@lexical/rich-text";
import { mergeRegister } from "@lexical/utils";
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  $isRootNode,
  COMMAND_PRIORITY_LOW,
  type ElementNode,
  KEY_BACKSPACE_COMMAND,
  KEY_ENTER_COMMAND,
} from "lexical";
import { useLexicalComposer } from "lexical-vue";
import { onMounted, onUnmounted } from "vue";

const editor = useLexicalComposer();

const exitToParent = (child: ElementNode, parent: ElementNode) => {
  if ($isRootNode(parent)) {
    const paragraph = $createParagraphNode();
    child.insertAfter(paragraph);
    paragraph.select();
  } else {
    parent.selectEnd();
  }
};

const exitQuote = (quote: ElementNode) => {
  const parent = quote.getParent();
  if (!parent) {
    return;
  }

  if (!quote.getTextContent().trim()) {
    exitToParent(quote, parent);
    quote.remove();
    return;
  }

  const paragraph = $createParagraphNode();
  if ($isQuoteNode(parent)) {
    parent.append(paragraph);
  } else {
    quote.insertAfter(paragraph);
  }
  paragraph.select();
};

const removeQuoteLevel = (quote: ElementNode) => {
  const parent = quote.getParent();
  if (!parent) {
    return;
  }

  const children = quote.getChildren();

  if ($isRootNode(parent)) {
    const paragraph = $createParagraphNode();
    children.forEach((child) => paragraph.append(child));
    quote.replace(paragraph);
    paragraph.selectStart();
    return;
  }

  children.forEach((child) => quote.insertBefore(child));
  quote.remove();
  children[0]?.selectStart();
};

const handleEnter = (event: KeyboardEvent | null) => {
  const selection = $getSelection();
  if (!selection || !$isRangeSelection(selection) || !selection.isCollapsed()) {
    return false;
  }

  const node = selection.anchor.getNode();
  const parent = node.getParent();
  const hasText = node.getTextContent().trim();

  if (
    !event ||
    event.shiftKey ||
    !parent ||
    (!$isQuoteNode(node) && !$isQuoteNode(parent)) ||
    ($isQuoteNode(node) && hasText)
  ) {
    return false;
  }

  event.preventDefault();
  editor.update(() => {
    if ($isQuoteNode(node)) {
      exitToParent(node, parent);
      node.remove();
    } else if (hasText) {
      const paragraph = $createParagraphNode();
      parent.append(paragraph);
      paragraph.select();
    } else {
      node.remove();
      exitQuote(parent);
    }
  });

  return true;
};

const handleBackspace = (event: KeyboardEvent) => {
  const selection = $getSelection();
  if (!selection || !$isRangeSelection(selection) || !selection.isCollapsed() || selection.anchor.offset) {
    return false;
  }

  const node = selection.anchor.getNode();
  const parent = node.getParent();

  if (!parent || !$isQuoteNode(parent)) {
    return false;
  }

  event.preventDefault();
  editor.update(() => {
    if ($isQuoteNode(node)) {
      if (!node.getTextContent().trim()) {
        exitToParent(node, parent);
        node.remove();
      } else {
        removeQuoteLevel(node);
      }
      return;
    }

    const grandParent = parent.getParent();
    if (!grandParent) {
      return;
    }

    const hasText = node.getTextContent().trim();

    if (parent.getChildrenSize() === 1 && !hasText) {
      exitToParent(parent, grandParent);
      parent.remove();
    } else if (!hasText) {
      node.remove();
      if (parent.getChildrenSize() === 0) {
        exitToParent(parent, grandParent);
        parent.remove();
      }
    } else {
      removeQuoteLevel(parent);
    }
  });

  return true;
};

let unregisterListeners: () => void;

onMounted(() => {
  unregisterListeners = mergeRegister(
    editor.registerCommand(KEY_ENTER_COMMAND, handleEnter, COMMAND_PRIORITY_LOW),
    editor.registerCommand(KEY_BACKSPACE_COMMAND, handleBackspace, COMMAND_PRIORITY_LOW)
  );
});

onUnmounted(() => {
  unregisterListeners?.();
});
</script>

<template>
  <slot />
</template>
